import { STATUS_REPORT_OPTIONS } from '@/shared/config';
import { t } from 'ttag';
import { SORT_INBOX_KEYS } from '@/entities/inbox';

/**
 * Builds the placeholder text of the inbox report-status filter from the current selection. Shows 'Status: All' when every entry of STATUS_REPORT_OPTIONS is selected, the single status name when one is selected, and 'Status: Multiple' otherwise.
 *
 * @param listStatusReport - Currently selected report statuses.
 * @returns Translated placeholder label for the status filter.
 */
const renderPlaceHolderStatusReport = (listStatusReport: any[]) => {
  if (listStatusReport.length === STATUS_REPORT_OPTIONS.length) {
    return t`Status: All`;
  }
  if (listStatusReport.length === 1) {
    return t`Status: ${listStatusReport[0]}`;
  }
  return t`Status: Multiple`;
};

/**
 * Translates an inbox sort selection into the sortBy, sortOrder and sortByText the report query and table use. Maps the new-to-old, old-to-new, high-to-low and low-to-high keys onto SEND_AT or PRIORITY with the matching direction and the date or priorityRank field, defaulting to newest SEND_AT first.
 *
 * @param sortValue - Sort key chosen in the inbox search bar.
 * @returns Sort descriptor holding the server field, the direction and the local sort field.
 */
const formatSortValue = (sortValue: string) => {
  switch (sortValue) {
    case SORT_INBOX_KEYS.NEW_TO_OLD:
      return {
        sortBy: 'SEND_AT',
        sortOrder: 'desc',
        sortByText: 'date',
      };
    case SORT_INBOX_KEYS.OLD_TO_NEW:
      return {
        sortBy: 'SEND_AT',
        sortOrder: 'asc',
        sortByText: 'date',
      };
    case SORT_INBOX_KEYS.HIGH_TO_LOW:
      return {
        sortBy: 'PRIORITY',
        sortOrder: 'desc',
        sortByText: 'priorityRank',
      };
    case SORT_INBOX_KEYS.LOW_TO_HIGH:
      return {
        sortBy: 'PRIORITY',
        sortOrder: 'asc',
        sortByText: 'priorityRank',
      };
    default:
      return {
        sortBy: 'SEND_AT',
        sortOrder: 'desc',
        sortByText: 'date',
      };
  }
};

/**
 * Recovers the inbox sort-dropdown key from a stored sortBy and sortOrder pair. Maps SEND_AT to the new-to-old or old-to-new key and PRIORITY to the high-to-low or low-to-high key by direction, falling back to new-to-old for anything else.
 *
 * @param sortBy - Server sort field, SEND_AT or PRIORITY.
 * @param sortOrder - Sort direction, 'desc' for descending and anything else ascending.
 * @returns The matching inbox sort key.
 */
const getSortValueBySortOrder = (sortBy: string, sortOrder: string) => {
  if (sortBy === 'SEND_AT') {
    return sortOrder === 'desc' ? SORT_INBOX_KEYS.NEW_TO_OLD : SORT_INBOX_KEYS.OLD_TO_NEW;
  }
  if (sortBy === 'PRIORITY') {
    return sortOrder === 'desc' ? SORT_INBOX_KEYS.HIGH_TO_LOW : SORT_INBOX_KEYS.LOW_TO_HIGH;
  }
  return SORT_INBOX_KEYS.NEW_TO_OLD;
};

export { renderPlaceHolderStatusReport, formatSortValue, getSortValueBySortOrder };
